"use client";

import React, { useCallback, useEffect, useState } from "react";
import { TableContainer } from "@/components/common/TableContainer";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { columns } from "./columns";
import { fetchWholesalersRequest } from "@/store/slices/wholesaler.slice";
import { DataView } from "@/components/common/DataView";
import { useNavigate, useParams } from "react-router-dom";

export interface WholesalerFilters {
  category?: string;
  onboarding_completed?: boolean;
  niche?: string;
}

export default function WholesalerPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const storeType = params.storeType as string;
  const storeId = params.storeId as string;
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [appliedFilters] = useState<WholesalerFilters>({});
  const pageSize = 10;

  const { items: list, pagination, loading, error } = useAppSelector(
    (state) => state.wholesaler,
  );

  const fetchWholesalers = useCallback(() => {
    const hasSearch = searchQuery.trim();

    const requestParams: any = {
      page: pageIndex + 1,
      pageSize: pageSize,
      filters: {
        ...appliedFilters,
      },
    };

    if (hasSearch) requestParams.filters.name = searchQuery;

    dispatch(fetchWholesalersRequest(requestParams));
  }, [appliedFilters, searchQuery, pageIndex, dispatch]);

  const currentData = list || [];
  const currentPagination = {
    totalPages: pagination?.totalPages || 1,
    currentPage: (pagination?.page || 0) + 1,
    totalItems: pagination?.total || 0,
    pageSize: pagination?.pageSize || 10,
    total: pagination?.total || 0,
  };
  const currentLoading = loading;

  useEffect(() => {
    fetchWholesalers();
  }, [fetchWholesalers]);

  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery, appliedFilters]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  };

  const handleRowClick = (wholesaler: any) => {
    if (!params) return;
    navigate(`/${storeType}/${storeId}/product/wholesaler/${wholesaler.id}`);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  const breadcrumbItems = [
    { label: "Products", href: `/${storeType}/${storeId}/product` },
    { label: "Wholesalers", isActive: true },
  ];

  return (
    <div className="space-y-4">
      <Breadcrumb items={breadcrumbItems} showBackButton={false} />
      <TableContainer>
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns as any}
            data={currentData}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={currentLoading || false}
            error={error?.message || null}
            title="Wholesalers"
            description="Browse and manage wholesaler partnerships"
            searchPlaceholder="Search wholesalers..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            onRowClick={handleRowClick}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyMessage="No wholesalers found."
            loadingMessage="Loading..."
          />
        </div>
      </TableContainer>
    </div>
  );
}
